import { Flex, Form, Input, type FormProps } from 'antd'
import { sum } from 'lodash-es'
import type { FunctionComponent } from 'react'
import styled from 'styled-components'
import CollaborativeItemFormList from '@/components/common/collaborative/collaborative-item-form-list'
import { type CollaborativeItemTypeCascaderProps } from '@/components/common/collaborative/collaborative-item-type-cascader'
import textMaximumLength from '@/constants/text-maximum-length'
import useFormatPrice from '@/hooks/use-format-price'
import useLocalizedKolQuotationCurrency from '@/hooks/use-localized-kol-quotation-currency'
import { useIntl } from '@/i18n/hooks/use-intl'
import type { Optional } from '@/types/optional'
import {
  draftQuotationSchema,
  quotationSchema,
  type DraftQuotation,
} from '@/types/schema/directory/quotation'

type DisabledOptions = NonNullable<
  CollaborativeItemTypeCascaderProps['disabledOptions']
>

const QuotationForm: FunctionComponent<
  Omit<FormProps<DraftQuotation>, 'layout'>
> = ({ form, preserve = false, ...props }) => {
  const { formatMessage } = useIntl()
  const { formatPrice } = useFormatPrice()

  const localizedKolQuotationCurrency = useLocalizedKolQuotationCurrency()

  const itemListValue = Form.useWatch(
    draftQuotationSchema.keyof().enum.item_list,
    form,
  )

  const disabledOptions: Optional<DisabledOptions> = itemListValue
    ?.filter(
      (item) => !item.is_custom_platform && !item.is_custom_collaborative_item,
    )
    .reduce((options: DisabledOptions, item) => {
      const optionsByPlatform = options.find(
        (option) => option.platform === item.platform_name,
      ) ?? {
        platform: item.platform_name,
        items: [],
      }

      optionsByPlatform.items.push(item.collaborative_item)

      return [
        ...options.filter((option) => option.platform !== item.platform_name),
        optionsByPlatform,
      ]
    }, [])
  const totalFee = sum(itemListValue?.map((item) => item.cooperation_fee))

  return (
    <Form {...props} form={form} layout='vertical' preserve={preserve}>
      <Form.Item
        label={formatMessage({
          id: 'kol_cooperation_detail_drawer:quotation_name',
        })}
        name={quotationSchema.keyof().enum.name}
        rules={[
          {
            required: true,
            message: formatMessage(
              {
                id: 'error:please_fulfill',
              },
              {
                inputField: formatMessage({
                  id: 'kol_cooperation_detail_drawer:quotation_name',
                }),
              },
            ),
          },
          {
            max: textMaximumLength.title,
            message: formatMessage(
              {
                id: 'error:input_filed_maximum_characters',
              },
              {
                limit: textMaximumLength.title,
              },
            ),
          },
        ]}
      >
        <Input
          placeholder={formatMessage({
            id: 'kol_cooperation_detail_drawer:quotation_name',
          })}
          size='small'
        />
      </Form.Item>
      <FormItem
        required
        label={
          <Flex align='center' justify='space-between'>
            {formatMessage({ id: 'campaign:cooperation_title' })}
            <CurrencyLabel>{localizedKolQuotationCurrency}</CurrencyLabel>
          </Flex>
        }
      >
        <CollaborativeItemFormList
          disabledItemTypeCascaderOptions={disabledOptions}
          name={quotationSchema.keyof().enum.item_list}
          totalFee={[
            formatMessage({ id: 'general:total_text' }),
            formatMessage({ id: 'general:colon' }),
            formatPrice(totalFee ?? 0),
          ].join('')}
        />
      </FormItem>
    </Form>
  )
}

const FormItem = styled(Form.Item)`
  .ant-form-item-label > label {
    width: 100%;

    &::after {
      display: none;
    }

    .ant-flex {
      width: 100%;
    }
  }
`

const CurrencyLabel = styled.div`
  color: ${({ theme }): string => theme.colors.text.tooltip};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
`

export default QuotationForm
